import React, { useCallback, useEffect, useState } from 'react';
import CustomSnackbar from '../../../components/CustomSnackbar';
import { CrmUser } from '../../../common/CommonTypes';
import { useTranslations } from '../../../localization/useTranslations';
import { openLoginModal } from '../../../common/Auth';
import Button from '../SimpleButton/Button';
import { getTextsForPremiumUnlockCZ, getTextsForPremiumUnlockSK } from '../helpers/textUtils';

declare global {
  interface Window {
    crm_user?: CrmUser;
    lockedForFreeUsers?: boolean;
    postID?: string;
  }
}

export default function PremiumUnlockButton() {
  const translations = useTranslations();
  // Check if the free articles feature is enabled
  const isFeatureEnabled = window?.isFreeArticlesFeatureEnabled || false;

  // States to hold the texts displayed in the component
  const [headingText, setHeadingText] = useState<string>('');
  const [descriptionText, setDescriptionText] = useState<string>('');
  const [buttonText, setButtonText] = useState<string>('');
  const [secondButtonText, setSecondButtonText] = useState<string>();
  // State to track the number of unlocked articles in the last month
  const [unlockedArticlesCount, setUnlockedArticlesCount] = useState<number>(0);
  const [isLoading] = useState<boolean>(false);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(
    () => window.sessionStorage.getItem(`${window.postID}.opened`) === 'true'
  );
  const [snackbarMessage, setSnackbarMessage] = useState<string>(
    translations?.snackBarMessages?.unlockArticle || 'Unlocked successfully!'
  );
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(window.crm_user ? true : false);

  // Get the unlocked articles from the global crm_user object
  const unlockedArticles = window.crm_user?.metering_article_unlocks;
  // Get the subscription slug from the global object or use a default value
  const subscriptionSlug = window?.subscriptionPageSlug || 'predplatne';
  const country = window?.country || 'cz';

  const premiumOptionalLinkLabel = window?.premiumOptionalLink?.premiumOptionalLinkLabel;
  const premiumOptionalLinkUrl = window?.premiumOptionalLink?.premiumOptionalLinkUrl;

  // Calculate the number of articles unlocked in the last 31 days
  const getUnlockedArticlesFromTheLastMonth = useCallback(() => {
    if (!unlockedArticles) return;

    const now = new Date();
    const thirtyOneDaysAgo = new Date();
    thirtyOneDaysAgo.setDate(now.getDate() - 31);

    let unlockedArticlesInTheLastMonth = 0;

    // Count the number of articles unlocked in the last month
    for (const article of unlockedArticles) {
      const articleDate = new Date(article.unlocked_at);
      if (articleDate > thirtyOneDaysAgo) {
        unlockedArticlesInTheLastMonth++;
      }
    }

    setUnlockedArticlesCount(unlockedArticlesInTheLastMonth);
  }, [unlockedArticles]);

  const handleUnlockClick = () => {
    if (isLoggedIn) {
      if (window.postID) {
        window.sessionStorage.setItem(`${window.postID}.opened`, 'true');
        window.location.reload();
        return;
      }

      // Show the content using CSS
      const premiumBlockerElement = document.querySelector('.premium-blocker') as HTMLElement;
      if (premiumBlockerElement) {
        premiumBlockerElement.style.display = 'none';
      }

      const softPaywallContent = document.querySelector('.post-content-soft-paywall') as HTMLElement;
      if (softPaywallContent) {
        softPaywallContent.style.display = 'block';
      }

      setUnlockedArticlesCount(unlockedArticlesCount + 1);
      setSnackbarMessage(translations?.snackBarMessages?.unlockArticle || 'Unlocked successfully!');
      setSnackbarOpen(true);
    } else {
      // If the user is not logged in, open the login modal
      openLoginModal();
    }
  };

  // Set the texts to be displayed based on the user's status and feature state
  // Function to select the correct text function based on the country
  const setTexts = useCallback(() => {
    let texts;

    switch (country) {
      case 'sk':
        texts = getTextsForPremiumUnlockSK(isLoggedIn, translations);
        break;
      case 'cz':
      default:
        texts = getTextsForPremiumUnlockCZ(
          isFeatureEnabled,
          isLoggedIn,
          unlockedArticlesCount,
          translations,
          window.lockedForFreeUsers || false
        );
        break;
    }

    setHeadingText(texts.headingText);
    setDescriptionText(texts.descriptionText);
    setButtonText(texts.buttonText);
    setSecondButtonText(texts.secondButtonText);
  }, [country, isLoggedIn, translations, isFeatureEnabled, unlockedArticlesCount]);

  // useEffect to calculate unlocked articles and set texts when component mounts or dependencies change
  useEffect(() => {
    getUnlockedArticlesFromTheLastMonth();
    setTexts();

    // Check if the snackbar message is stored in localStorage
    const storedMessage = localStorage.getItem('snackbarMessage');
    if (storedMessage) {
      setTimeout(() => {
        // Delay showing snackbar message to avoid it disappearing too quickly
        setSnackbarMessage(storedMessage);
        setSnackbarOpen(true);
        localStorage.removeItem('snackbarMessage'); // Clear it once displayed
      }, 500);
    }
  }, [getUnlockedArticlesFromTheLastMonth, setTexts, isFeatureEnabled]);

  // Automatically unlock articles that were previously unlocked
  useEffect(() => {
    if (unlockedArticles && unlockedArticles.length > 0 && country === 'sk') {
      // Delay execution to ensure DOM is loaded
      unlockedArticles.forEach((article) => {
        let articleSlug = article.article_external_id.trim();

        if (articleSlug.startsWith('/')) {
          articleSlug = articleSlug.substring(1);
        }
        if (articleSlug.endsWith('/')) {
          articleSlug = articleSlug.slice(0, -1);
        }

        const articleElement = document.querySelector(`[data-article-slug="${articleSlug}"]`) as HTMLElement;

        if (articleElement) {
          // Hide/remove the premium blocker
          const premiumBlockerElement = articleElement.querySelector('.premium-blocker') as HTMLElement;
          if (premiumBlockerElement) {
            premiumBlockerElement.style.display = 'none';
          }

          // Show the soft paywall content
          const softPaywallContent = articleElement.querySelector('.post-content-soft-paywall') as HTMLElement;
          if (softPaywallContent) {
            softPaywallContent.style.display = 'block';
          }
        }
      });
    }
  }, [country, unlockedArticles]);

  const onCloseSnackbar = useCallback(() => {
    setSnackbarOpen(false);
  }, []);

  useEffect(() => {
    if (window.crm_user) {
      setIsLoggedIn(true);
    } else {
      setIsLoggedIn(false);
    }
  }, []);

  return (
    <>
      <CustomSnackbar message={snackbarMessage} open={snackbarOpen} onClose={onCloseSnackbar} />
      <div className={`premium-blocker premium-blocker--${country}`} id="remp_cta" data-needed="web">
        <h4 className="heading heading--archivo heading--700">{headingText}</h4>
        <p className="premium-blocker__description">{descriptionText}</p>
        <div className="premium-blocker__button-wrapper">
          {/* Primary button for unlocking the article or subscribing */}
          <Button
            className=""
            label={buttonText}
            type="primary"
            size="large"
            onClick={handleUnlockClick}
            isDisabled={isLoading}
            fullWidth={false}
            iconName="lock"
          />
          {/* Optional link to navigate to another page (only SK) */}
          {premiumOptionalLinkLabel && premiumOptionalLinkUrl && country === 'sk' && (
            <a href={premiumOptionalLinkUrl} className="link link-underline-14">
              <span className="link__text">{premiumOptionalLinkLabel}</span>
            </a>
          )}

          {/* Second button, displayed only if user has not reached the limit */}
          {secondButtonText && unlockedArticlesCount < 3 && (
            <Button
              className=""
              label={secondButtonText}
              type="secondary"
              size="large"
              onClick={() => (window.location.href = `/${subscriptionSlug}`)}
              isDisabled={isLoading}
              fullWidth={false}
            />
          )}
        </div>
      </div>
    </>
  );
}
